import React from 'react';
import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import WidgetCard from './WidgetCard';
import { formatServerDate, useWidgetData } from './useWidgetData';

interface IncomingDoc {
  id: number;
  statusName: string;
  sender: string;
  recipient: string;
  viaWarehouse: string | null;
  /** null - сумма пока недоступна (сервис номенклатуры недоступен) */
  amount: number | null;
  updatedAt: string;
}

interface IncomingResponse {
  documents: IncomingDoc[];
  limit: number;
}

const ACCENT = '#2563eb';
const headCell = { fontSize: 12, fontWeight: 600, color: '#64748b', bgcolor: '#f8fafc', whiteSpace: 'nowrap' as const, py: 0.75 };
const cell = { fontSize: 13, color: '#0f172a', py: 1 };

/** Виджет «Входящие документы» (статус «В пути»). Право: home.documents.incoming.view (+ .limit) */
const IncomingDocumentsWidget: React.FC = () => {
  const { data, loading, error, reload } = useWidgetData<IncomingResponse>('/api/v2/home/documents/incoming');
  const docs = data?.documents || [];

  return (
    <WidgetCard
      title="Входящие документы"
      icon={<LocalShippingOutlinedIcon />}
      accent={ACCENT}
      loading={loading}
      error={error}
      onRefresh={reload}
    >
      {!loading && docs.length === 0 ? (
        <Typography sx={{ p: 2, fontSize: 13, color: '#94a3b8' }}>Документов в пути нет</Typography>
      ) : (
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCell, pl: 2 }}>№ документа</TableCell>
              <TableCell sx={headCell}>Отправитель</TableCell>
              <TableCell sx={headCell}>Получатель</TableCell>
              <TableCell sx={headCell}>Статус</TableCell>
              <TableCell sx={headCell} align="right">Сумма</TableCell>
              <TableCell sx={{ ...headCell, pr: 2 }}>Обновлён</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {docs.map(d => (
              <TableRow key={d.id} hover>
                <TableCell sx={{ ...cell, pl: 2, fontWeight: 600 }}>{d.id}</TableCell>
                <TableCell sx={cell}>{d.sender || '-'}</TableCell>
                <TableCell sx={cell}>{d.recipient || '-'}</TableCell>
                <TableCell sx={cell}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Chip size="small" label={d.statusName} sx={{ height: 22, bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 500 }} />
                    {d.viaWarehouse && (
                      <Typography sx={{ fontSize: 12, color: '#64748b' }}>→ {d.viaWarehouse}</Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={cell} align="right">
                  {d.amount != null ? d.amount.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) : '-'}
                </TableCell>
                <TableCell sx={{ ...cell, pr: 2, color: '#64748b', whiteSpace: 'nowrap' }}>{formatServerDate(d.updatedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </WidgetCard>
  );
};

export default IncomingDocumentsWidget;
